import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

// Hold off briefly before showing so preloaded (instant) navigations — which
// resolve in well under this — never flash the bar. It only appears when a
// loader genuinely takes a moment (cold/slow API).
const SHOW_DELAY_MS = 120;
const FINISH_FADE_MS = 240;
const TRICKLE_MS = 220;

/**
 * Thin top-of-viewport progress bar tied to the router's navigation state.
 *
 * Route loaders prefetch the explore-grid data, so the router keeps the old
 * page on screen during navigation instead of flashing the full-page Suspense
 * spinner. On a fast/preloaded switch that's seamless — but on a slow
 * connection the page would sit frozen with no feedback. This bar fills the gap:
 * it trickles toward 90% while `isLoading`, then snaps to 100% and fades.
 */
export function RouteProgressBar() {
  const isLoading = useRouterState({ select: (s) => s.isLoading });
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  const showTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const trickle = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  useEffect(() => {
    if (isLoading) {
      clearTimeout(hideTimer.current);
      showTimer.current = setTimeout(() => {
        setVisible(true);
        setProgress(8);
        trickle.current = setInterval(() => {
          // Ease toward 90% and stall there until the navigation resolves.
          setProgress((p) => (p >= 90 ? p : p + Math.max(0.5, (90 - p) * 0.1)));
        }, TRICKLE_MS);
      }, SHOW_DELAY_MS);
    } else {
      clearTimeout(showTimer.current);
      clearInterval(trickle.current);
      // Snap to full then fade — but only if the bar actually showed.
      setProgress((p) => (p > 0 ? 100 : 0));
      hideTimer.current = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, FINISH_FADE_MS);
    }
    return () => {
      clearTimeout(showTimer.current);
      clearInterval(trickle.current);
    };
  }, [isLoading]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]"
    >
      <div
        className="h-full rounded-r-full bg-[color:var(--color-primary)] shadow-[0_0_10px_rgba(4,0,51,0.45)] transition-[width,opacity] duration-200 ease-out"
        style={{ width: `${progress}%`, opacity: progress >= 100 ? 0 : 1 }}
      />
    </div>
  );
}
